import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useApproveApproval } from "@/hooks/security/useApprovals";
import type { ApprovalRequestDTO } from "@/types/lighthouseApprovals";

const PRESETS: Array<{ label: string; value: string; days?: number }> = [
  { label: "Never", value: "never" },
  { label: "24 hours", value: "24h", days: 1 },
  { label: "7 days", value: "7d", days: 7 },
  { label: "30 days", value: "30d", days: 30 },
  { label: "90 days", value: "90d", days: 90 },
];

function presetToIso(value: string): string | undefined {
  const p = PRESETS.find((p) => p.value === value);
  if (!p?.days) return undefined;
  return new Date(Date.now() + p.days * 24 * 3600 * 1000).toISOString();
}

interface Props {
  request: ApprovalRequestDTO | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone?: () => void;
}

export function ApproveDialog({ request, open, onOpenChange, onDone }: Props) {
  const [reason, setReason] = useState("");
  const [expires, setExpires] = useState("never");
  const [restrictDevice, setRestrictDevice] = useState(false);
  const mut = useApproveApproval();

  if (!request) return null;
  const hostname = request.agent?.hostname ?? request.deviceId ?? "—";
  const name = request.agent?.displayName ?? request.agentId;

  const submit = () => {
    const conditions: Record<string, unknown> = {};
    const iso = presetToIso(expires);
    if (iso) conditions.expires_at = iso;
    if (restrictDevice && request.deviceId)
      conditions.only_on_devices = [request.deviceId];
    mut.mutate(
      { id: request.id, reason: reason || undefined, conditions },
      {
        onSuccess: () => {
          setReason("");
          setExpires("never");
          setRestrictDevice(false);
          onOpenChange(false);
          onDone?.();
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Approve "{name} on {hostname}"?</DialogTitle>
          <DialogDescription>
            Sanction this agent. You can attach conditions below.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              Reason (optional)
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Why this is OK to sanction…"
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              Expires in
            </label>
            <Select value={expires} onValueChange={setExpires}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRESETS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {request.deviceId && (
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={restrictDevice}
                onCheckedChange={(v) => setRestrictDevice(v === true)}
              />
              Restrict to current device only
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={mut.isPending}>
            Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
